import React, { useState, ChangeEvent, useEffect } from 'react';
import { Plane, Ship, Archive, Settings, Clock, AlertCircle, CheckCircle, Database, X, Lightbulb, RotateCcw, Search, Filter, Calendar } from 'lucide-react';
import { DEFAULT_AIR_RULES, DEFAULT_OCEAN_RULES, fetchAWBRule, fetchOceanRule, validateRules } from './archieve-service';
import { AirRules, ConfigTab, OceanRules, RecommendationRange, SearchMode, Shipment, Statistics } from './type-model';
import { axiosInstance } from '../../utility/apiInstance';
import toast from 'react-hot-toast';
import ArchieveTable from './ArchieveTable';
import StatisticsCards from './StatisticsCards';
import './archieve-management.css';



const ArchiveManagementPage: React.FC = () => {
  // State Management
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);
  const [activeConfigTab, setActiveConfigTab] = useState<ConfigTab>('ocean');

  const [airRules, setAirRules] = useState(DEFAULT_AIR_RULES);
  const [oceanRules, setOceanRules] = useState(DEFAULT_OCEAN_RULES);

  useEffect(() => {
    const loadRules = async () => {
      try {
        const [air, ocean] = await Promise.all([
          fetchAWBRule(),
          fetchOceanRule(),
        ]);

        setAirRules(air);
        setOceanRules(ocean);
      } catch (error) {
        console.error("Failed to load archival rules", error);
      }
    };

    loadRules();
  }, []);


  // Recommendation Ranges
  const oceanRecommendations: Record<keyof OceanRules, RecommendationRange> = {
    _id: { min: 1, max: 100, typical: 1 },
    inactive: { min: 20, max: 30, typical: 25 },
    gateOut: { min: 10, max: 15, typical: 12 },
    containerReturn: { min: 7, max: 10, typical: 7 }
  };

  const airRecommendations: Record<keyof AirRules, RecommendationRange> = {
    _id: { min: 1, max: 100, typical: 1 },
    inactive: { min: 10, max: 15, typical: 12 },
    arrival: { min: 2, max: 3, typical: 2 },
    delivered: { min: 1, max: 2, typical: 2 }
  };



  // Event Handlers
  const handleUpdateOcean = (field: keyof OceanRules, value: string): void => {
    setOceanRules(prev => ({ ...prev, [field]: parseInt(value) || 0 }));
  };

  const handleUpdateAir = (field: keyof AirRules, value: string): void => {
    setAirRules(prev => ({ ...prev, [field]: parseInt(value) || 0 }));
  };

  const handleResetToDefaults = (): void => {
    if (activeConfigTab === 'ocean') {
      setOceanRules((prev) => ({
        ...prev,
        inactive: oceanRecommendations.inactive.typical,
        gateOut: oceanRecommendations.gateOut.typical,
        containerReturn: oceanRecommendations.containerReturn.typical
      }));
    } else {
      setAirRules((prev) => ({
        ...prev,
        inactive: airRecommendations.inactive.typical,
        arrival: airRecommendations.arrival.typical,
        delivered: airRecommendations.delivered.typical
      }));
    }
  };

  const isInRecommendedRange = (value: number, field: string): boolean => {
    const rec = activeConfigTab === 'ocean'
      ? oceanRecommendations[field as keyof OceanRules]
      : airRecommendations[field as keyof AirRules];
    return rec ? value >= rec.min && value <= rec.max : false;
  };

  

  const handleSaveRules = async (): Promise<void> => {
    try {
      if (activeConfigTab === 'ocean') {

        const error = validateRules(oceanRules, [
          'inactive',
          'gateOut',
          'containerReturn',
        ]);

        if (error) {
          toast.error(error);
          return;
        }

        const payload = {
          daysAfterInactive: oceanRules.inactive,
          daysAfterGateOut: oceanRules.gateOut,
          daysAfterContainerReturn: oceanRules.containerReturn,
        };

        if (oceanRules._id) {
          const { data } = await axiosInstance.put(
            `/api/v1/ocean-archival-rule/${oceanRules._id}`,
            payload
          );
          toast.success(data?.message);
        } else {
          const { data } = await axiosInstance.post(
            '/api/v1/ocean-archival-rule',
            payload
          );
          toast.success(data?.message);
        }

      } else {

        const error = validateRules(airRules, [
          'inactive',
          'arrival',
          'delivered',
        ]);

        if (error) {
          toast.error(error);
          return;
        }

        const payload = {
          daysAfterInactive: airRules.inactive,
          daysAfterArrival: airRules.arrival,
          daysAfterDelivered: airRules.delivered,
        };

        if (airRules._id) {
          const { data } = await axiosInstance.put(
            `/api/v1/archival-rules/${airRules._id}`,
            payload
          );
          toast.success(data?.message);
        } else {
          const { data } = await axiosInstance.post(
            '/api/v1/archival-rules',
            payload
          );
          toast.success(data?.message);
        }
      }

      setShowConfigModal(false);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.[0]?.message ||
        error.response?.data?.error ||
        'Failed to save archival rules';

      toast.error(errorMessage);
    }
  };


  return (
    <div className="archiveMain">
      {/* Header */}
      <div className="title_ArchiveOuter">
        <div className="title_Archive_Left">
          <div className="title_Archive">
            <div style={{ backgroundColor: '#F8B71F' }} className="p-2 rounded-lg">
              <Archive className="w-4 h-4 text-white" />
            </div>
            <h1>Archive Management</h1>
          </div>
          <p>Monitor archived shipments and configure archival policies</p>
        </div>
        <div className="title_Archive_Right">
          <button
            onClick={() => setShowConfigModal(true)}
            className="btn btnYellow"
          >
            <Settings className="w-4 h-4" />
            Configure Rules
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <StatisticsCards />
      {/* Archived Shipments Section */}
      <ArchieveTable />

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
          <div className="bg-white h-full w-[600px] shadow-2xl flex flex-col max-w-full animate-slide-in-right">
            {/* Modal Header */}
            <div style={{ backgroundColor: '#F8B71F' }} className="px-3 sm:px-4 md:px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-white" />
                  <h2 className="text-lg font-semibold text-white">Archive Rules Configuration</h2>
                </div>
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-6 bg-gray-50 drawerArchieveManagement">
              <div className="flex justify-between items-center">
                <div className="flex gap-1">
                  <button
                    onClick={() => setActiveConfigTab('ocean')}
                    style={{
                      color: activeConfigTab === 'ocean' ? '#F8B71F' : '#777779',
                      borderBottomColor: activeConfigTab === 'ocean' ? '#F8B71F' : 'transparent',
                      backgroundColor: activeConfigTab === 'ocean' ? 'white' : 'transparent',
                    }}
                    className="flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors"
                  >
                    <Ship className="w-4 h-4" />
                    Ocean
                  </button>
                  <button
                    onClick={() => setActiveConfigTab('air')}
                    style={{
                      color: activeConfigTab === 'air' ? '#F8B71F' : '#777779',
                      borderBottomColor: activeConfigTab === 'air' ? '#F8B71F' : 'transparent',
                      backgroundColor: activeConfigTab === 'air' ? 'white' : 'transparent',
                    }}
                    className="flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors"
                  >
                    <Plane className="w-4 h-4" />
                    Air
                  </button>
                </div>
                <button
                  onClick={handleResetToDefaults}
                  className="btn btnBlack"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
              </div>
            </div>

            {/* Modal Content - Ocean Rules */}
            <div className="flex-1 overflow-y-auto drawerAchieveBody">
              {activeConfigTab === 'ocean' ? (
                <div className="space-y-6">
                  {/* Ocean Rule 1 - Inactive */}
                  <div className="drawerArchieveBox">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h4 style={{ color: '#333435' }} className="font-semibold mb-1">No Tracking Updates (Inactive)</h4>
                        <p style={{ color: '#777779' }} className="text-sm">Archive containers showing no tracking activity from carrier</p>
                      </div>
                    </div>
                    <div className="space-y-3 ml-11">
                      <div className="flex items-center gap-3">
                        <label style={{ color: '#333435' }} className="text-sm font-medium w-32">Days after last update:</label>
                        <input
                          type="number"
                          value={oceanRules.inactive}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateOcean('inactive', e.target.value)}
                          style={{
                            borderColor: isInRecommendedRange(oceanRules.inactive, 'inactive') ? '#4ade80' : '#d1d5db',
                            backgroundColor: isInRecommendedRange(oceanRules.inactive, 'inactive') ? '#f0fdf4' : 'white',
                          }}
                          className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-opacity-50"
                          onFocus={(e) => e.currentTarget.style.borderColor = '#F8B71F'}
                          onBlur={(e) => e.currentTarget.style.borderColor = isInRecommendedRange(oceanRules.inactive, 'inactive') ? '#4ade80' : '#d1d5db'}
                          min={1}
                        />
                        <span style={{ color: '#777779' }} className="text-sm">days</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs bg-amber-50 p-3 rounded-lg border">
                        <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>
                          <span style={{ color: '#92400e' }} className="font-medium">Recommended: {oceanRecommendations.inactive.min}-{oceanRecommendations.inactive.max} days</span>
                          <br />
                          <span style={{ color: '#777779' }}>Typical ocean booking lead time consideration</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Ocean Rule 2 - Gate Out */}
                  <div className="drawerArchieveBox">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 style={{ color: '#333435' }} className="font-semibold mb-1">Gate Out Completed</h4>
                        <p style={{ color: '#777779' }} className="text-sm">Container exited from port terminal</p>
                      </div>
                    </div>
                    <div className="space-y-3 ml-11">
                      <div className="flex items-center gap-3">
                        <label style={{ color: '#333435' }} className="text-sm font-medium w-32">Days after gate out:</label>
                        <input
                          type="number"
                          value={oceanRules.gateOut}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateOcean('gateOut', e.target.value)}
                          style={{
                            borderColor: isInRecommendedRange(oceanRules.gateOut, 'gateOut') ? '#4ade80' : '#d1d5db',
                            backgroundColor: isInRecommendedRange(oceanRules.gateOut, 'gateOut') ? '#f0fdf4' : 'white',
                          }}
                          className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-opacity-50"
                          onFocus={(e) => e.currentTarget.style.borderColor = '#F8B71F'}
                          onBlur={(e) => e.currentTarget.style.borderColor = isInRecommendedRange(oceanRules.gateOut, 'gateOut') ? '#4ade80' : '#d1d5db'}
                          min={1}
                        />
                        <span style={{ color: '#777779' }} className="text-sm">days</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs bg-amber-50 p-3 rounded-lg border">
                        <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>
                          <span style={{ color: '#92400e' }} className="font-medium">Recommended: {oceanRecommendations.gateOut.min}-{oceanRecommendations.gateOut.max} days</span>
                          <br />
                          <span style={{ color: '#777779' }}>Standard inland transit + delivery window</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Ocean Rule 3 - Container Return */}
                  <div className="drawerArchieveBox">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h4 style={{ color: '#333435' }} className="font-semibold mb-1">Empty Container Return</h4>
                        <p style={{ color: '#777779' }} className="text-sm">Empty returned to depot/yard</p>
                      </div>
                    </div>
                    <div className="space-y-3 ml-11">
                      <div className="flex items-center gap-3">
                        <label style={{ color: '#333435' }} className="text-sm font-medium w-32">Days after return:</label>
                        <input
                          type="number"
                          value={oceanRules.containerReturn}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateOcean('containerReturn', e.target.value)}
                          style={{
                            borderColor: isInRecommendedRange(oceanRules.containerReturn, 'containerReturn') ? '#4ade80' : '#d1d5db',
                            backgroundColor: isInRecommendedRange(oceanRules.containerReturn, 'containerReturn') ? '#f0fdf4' : 'white',
                          }}
                          className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-opacity-50"
                          onFocus={(e) => e.currentTarget.style.borderColor = '#F8B71F'}
                          onBlur={(e) => e.currentTarget.style.borderColor = isInRecommendedRange(oceanRules.containerReturn, 'containerReturn') ? '#4ade80' : '#d1d5db'}
                          min={1}
                        />
                        <span style={{ color: '#777779' }} className="text-sm">days</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs bg-amber-50 p-3 rounded-lg border">
                        <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>
                          <span style={{ color: '#92400e' }} className="font-medium">Recommended: {oceanRecommendations.containerReturn.min}-{oceanRecommendations.containerReturn.max} days</span>
                          <br />
                          <span style={{ color: '#777779' }}>Grace period for demurrage reconciliation</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Air Rule 1 - Inactive */}
                  <div className="drawerArchieveBox">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h4 style={{ color: '#333435' }} className="font-semibold mb-1">No Tracking Updates (Inactive)</h4>
                        <p style={{ color: '#777779' }} className="text-sm">AWB hasn't received updates from carrier</p>
                      </div>
                    </div>
                    <div className="space-y-3 ml-11">
                      <div className="flex items-center gap-3">
                        <label style={{ color: '#333435' }} className="text-sm font-medium w-32">Days after last update:</label>
                        <input
                          type="number"
                          value={airRules.inactive}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateAir('inactive', e.target.value)}
                          style={{
                            borderColor: isInRecommendedRange(airRules.inactive, 'inactive') ? '#4ade80' : '#d1d5db',
                            backgroundColor: isInRecommendedRange(airRules.inactive, 'inactive') ? '#f0fdf4' : 'white',
                          }}
                          className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-opacity-50"
                          onFocus={(e) => e.currentTarget.style.borderColor = '#F8B71F'}
                          onBlur={(e) => e.currentTarget.style.borderColor = isInRecommendedRange(airRules.inactive, 'inactive') ? '#4ade80' : '#d1d5db'}
                          min={1}
                        />
                        <span style={{ color: '#777779' }} className="text-sm">days</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs bg-amber-50 p-3 rounded-lg border">
                        <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>
                          <span style={{ color: '#92400e' }} className="font-medium">Recommended: {airRecommendations.inactive.min}-{airRecommendations.inactive.max} days</span>
                          <br />
                          <span style={{ color: '#777779' }}>Typical air booking confirmation period</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Air Rule 2 - Arrival */}
                  <div className="drawerArchieveBox">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 style={{ color: '#333435' }} className="font-semibold mb-1">Arrival at Destination Airport</h4>
                        <p style={{ color: '#777779' }} className="text-sm">Cargo landed at destination airport</p>
                      </div>
                    </div>
                    <div className="space-y-3 ml-11">
                      <div className="flex items-center gap-3">
                        <label style={{ color: '#333435' }} className="text-sm font-medium w-32">Days after arrival:</label>
                        <input
                          type="number"
                          value={airRules.arrival}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateAir('arrival', e.target.value)}
                          style={{
                            borderColor: isInRecommendedRange(airRules.arrival, 'arrival') ? '#4ade80' : '#d1d5db',
                            backgroundColor: isInRecommendedRange(airRules.arrival, 'arrival') ? '#f0fdf4' : 'white',
                          }}
                          className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-opacity-50"
                          onFocus={(e) => e.currentTarget.style.borderColor = '#F8B71F'}
                          onBlur={(e) => e.currentTarget.style.borderColor = isInRecommendedRange(airRules.arrival, 'arrival') ? '#4ade80' : '#d1d5db'}
                          min={1}
                        />
                        <span style={{ color: '#777779' }} className="text-sm">days</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs bg-amber-50 p-3 rounded-lg border">
                        <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>
                          <span style={{ color: '#92400e' }} className="font-medium">Recommended: {airRecommendations.arrival.min}-{airRecommendations.arrival.max} days</span>
                          <br />
                          <span style={{ color: '#777779' }}>Customs clearance + pickup window</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Air Rule 3 - Delivered */}
                  <div className="drawerArchieveBox">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h4 style={{ color: '#333435' }} className="font-semibold mb-1">Final Delivery Completed</h4>
                        <p style={{ color: '#777779' }} className="text-sm">Delivered to consignee</p>
                      </div>
                    </div>
                    <div className="space-y-3 ml-11">
                      <div className="flex items-center gap-3">
                        <label style={{ color: '#333435' }} className="text-sm font-medium w-32">Days after delivery:</label>
                        <input
                          type="number"
                          value={airRules.delivered}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpdateAir('delivered', e.target.value)}
                          style={{
                            borderColor: isInRecommendedRange(airRules.delivered, 'delivered') ? '#4ade80' : '#d1d5db',
                            backgroundColor: isInRecommendedRange(airRules.delivered, 'delivered') ? '#f0fdf4' : 'white',
                          }}
                          className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-opacity-50"
                          onFocus={(e) => e.currentTarget.style.borderColor = '#F8B71F'}
                          onBlur={(e) => e.currentTarget.style.borderColor = isInRecommendedRange(airRules.delivered, 'delivered') ? '#4ade80' : '#d1d5db'}
                          min={1}
                        />
                        <span style={{ color: '#777779' }} className="text-sm">days</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs bg-amber-50 p-3 rounded-lg border">
                        <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>
                          <span style={{ color: '#92400e' }} className="font-medium">Recommended: {airRecommendations.delivered.min}-{airRecommendations.delivered.max} days</span>
                          <br />
                          <span style={{ color: '#777779' }}>Grace period for POD confirmation</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="modalFooterConfigure">
              <p>Changes apply to future archival operations</p>
              <div className="modalFooterConfigureBtn gap-3">
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="btn btnBlack"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRules}
                  className="btn btnYellow"
                >
                  Save Rules
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ArchiveManagementPage;